import React, { useCallback } from 'react'

import { produce } from 'immer'
import { useQueryClient } from 'react-query'

import { Grid } from '@material-ui/core'

import ProductGridCard from '~/components/cards/ProductCard/ProductGridCard'
import ProductListCard from '~/components/cards/ProductCard/ProductListCard'

import { CACHE_KEYS } from '~/constants'
import { Listing, ListingResponse } from '~/models/Listing/interface'

import AdsSlot from '../../components/shared/AdSlot'

const adsMap = [
  { slotId: 'gam_nativemob_pos1', pos: '1', index: 4 },
  { slotId: 'gam_nativemob_pos2', pos: '2', index: 8 }
]

interface SearchProductListProps {
  orientation: 'grid' | 'list'
  listings: Listing[]
  loading?: boolean
}

const SearchProductList = ({
  orientation,
  listings,
  loading
}: SearchProductListProps) => {
  const queryClient = useQueryClient()

  const onFavorite = useCallback(
    ({ id, favorite }) => {
      queryClient.setQueryData<ListingResponse>(CACHE_KEYS.listings, (old) => {
        return produce(old, (draft) => {
          const index = draft.data.findIndex((data) => data.id === id)
          draft.data[index].favorite = favorite
        })
      })
    },
    [queryClient]
  )

  const listingsWithAds = listings.reduce((acc, listing, index) => {
    const item = adsMap.find((item) => item.index === index)
    if (item) {
      acc.push(item, listing)
    } else {
      acc.push(listing)
    }

    return acc
  }, [])

  const isGrid = orientation === 'grid'

  return (
    <>
      {listingsWithAds.map((el, idx) => {
        if (el?.slotId) {
          const key = `${el.slotId}-ad-${orientation}`

          return (
            <Grid
              item
              xs={12}
              sm={isGrid ? 6 : 12}
              id={key}
              key={key}
              style={{
                alignSelf: 'center',
                textAlign: 'center'
              }}
            >
              <AdsSlot
                slotId={el.slotId}
                sizes={[[300, 250]]}
                shouldRefresh={() => true}
                sizeMapping={[
                  {
                    viewport: [2250, 0],
                    sizes: [
                      [300, 250],
                      [320, 100],
                      [320, 50],
                      [300, 50],
                      'fluid'
                    ]
                  },

                  {
                    viewport: [0, 0],
                    sizes: [
                      [300, 250],
                      [320, 100],
                      [320, 50],
                      [300, 50],
                      'fluid'
                    ]
                  }
                ]}
                targetingArguments={{
                  pos: el.pos,
                  native: 'infeed'
                }}
              />
            </Grid>
          )
        }

        const key = `${el?.slug}-${`idx-${idx}`}-${orientation}`

        const handleFavorite = (favorite) => {
          onFavorite({ favorite, id: el?.id })
        }

        return isGrid ? (
          <Grid item xs={12} sm={6} key={key} id={key}>
            <ProductGridCard
              data={el}
              featured={el.featured}
              loading={loading}
              onFavorite={handleFavorite}
              dealerBox
            />
          </Grid>
        ) : (
          <Grid item xs={12} key={key} id={key}>
            <ProductListCard
              data={el}
              featured={el.featured}
              loading={loading}
              onFavorite={handleFavorite}
            />
          </Grid>
        )
      })}
    </>
  )
}

export default React.memo(SearchProductList)
